import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import ItemConfirmationStep from "./ItemConfirmationStep";
import ItemFormStep from "./ItemFormStep";
/* ------------------ Defaults ------------------ */

const DEFAULT_VALUES = {
  title: "",
  subTitle: "",
  desc: "",
  cardImg: "",
  type: "file",
  authGroup: "authenticated",
  embedURL: "",
  workspaceId: "",
  reportId: "",
  blobName: "",
  container: "",
  embedd: "",
  link: "",
};

const ItemFormModal = ({ isOpen, onClose, onSave, item }) => {
  const toast = useToast();
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formDataPreview, setFormDataPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingEvent, setPendingEvent] = useState(null);

  // Tracks whether the type watcher should clear fields.
  // Set to false on every modal open/reset so the initial type value
  // from reset() doesn't trigger a field wipe.
  const isTypeInitialized = useRef(false);

  // Alert dialog for upload confirmation
  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure();
  const cancelRef = useRef();

  const type = watch("type");
  const cardImg = watch("cardImg");
  const authGroup = watch("authGroup");

  /* ------------------ Sync edit / open ------------------ */

  useEffect(() => {
    // Reset the guard so the upcoming type change from reset() is skipped
    isTypeInitialized.current = false;

    if (item) {
      reset({ ...DEFAULT_VALUES, ...item });
      if (item.blobName && item.container) {
        setUploadedFile({ name: item.blobName });
      }
    } else {
      reset(DEFAULT_VALUES);
      setUploadedFile(null);
    }
    setCurrentStep(1);
  }, [item, isOpen, reset]);

  /* ------------------ Clear unused fields on type change ------------------ */

  useEffect(() => {
    // Skip the very first fire after reset() — only clear when user manually changes type
    if (!isTypeInitialized.current) {
      isTypeInitialized.current = true;
      return;
    }

    if (type !== "powerbi") {
      setValue("embedURL", "");
      setValue("workspaceId", "");
      setValue("reportId", "");
    }
    if (type !== "tableau") {
      setValue("embedd", "");
    }
    if (type !== "file") {
      setValue("blobName", "");
      setUploadedFile(null);
    }
    if (type !== "file" && type !== "directory") {
      setValue("container", "");
    }
    if (type !== "link" && type !== "Static") {
      setValue("link", "");
    }
  }, [type, setValue]);

  /* ------------------ File Upload Handler ------------------ */

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPendingFile(file);
    setPendingEvent(e);
    onAlertOpen();
  };

  const confirmUpload = async () => {
    if (!pendingFile || !pendingEvent) return;

    onAlertClose();
    setIsUploading(true);

    try {
      const response = await fetch(
        `/api/writeBlob?blob=${encodeURIComponent(pendingFile.name)}&authgroup=${authGroup}`,
        {
          method: "POST",
          body: pendingFile,
          headers: {
            "Content-Type": "application/octet-stream",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const result = await response.json();

      setValue("blobName", result.blob);
      setValue("container", result.container);
      setUploadedFile(pendingFile);

      toast({
        title: "Upload Successful",
        description: `File "${pendingFile.name}" uploaded successfully`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload file",
        status: "error",
        duration: 5000,
      });
      if (pendingEvent.target) {
        pendingEvent.target.value = "";
      }
    } finally {
      setIsUploading(false);
      setPendingFile(null);
      setPendingEvent(null);
    }
  };

  const cancelUpload = () => {
    if (pendingEvent?.target) {
      pendingEvent.target.value = "";
    }
    setPendingFile(null);
    setPendingEvent(null);
    onAlertClose();
  };

  /* ------------------ Prepare data for confirmation ------------------ */

  const prepareFormData = (data) => {
    const cleanedData = {
      title: data.title.trim(),
      subTitle: data.subTitle?.trim(),
      desc: data.desc?.trim(),
      type: data.type,
      authGroup: data.authGroup,
      cardImg: data.cardImg?.trim(),
    };

    if (data.type === "powerbi") {
      if (data.embedURL) cleanedData.embedURL = data.embedURL.trim();
      if (data.workspaceId) cleanedData.workspaceId = data.workspaceId.trim();
      if (data.reportId) cleanedData.reportId = data.reportId.trim();
    }

    if (data.type === "tableau") {
      cleanedData.embedd = data.embedd.trim();
    }

    if (data.type === "file") {
      cleanedData.blobName = data.blobName.trim();
      cleanedData.container = data.container.trim();
    }

    // Also persist container for directory type
    if (data.type === "directory") {
      cleanedData.container = data.container.trim();
    }

    if (data.type === "link" || data.type === "Static") {
      cleanedData.link = data.link?.trim();
    }

    Object.keys(cleanedData).forEach((k) => {
      if (cleanedData[k] === "") delete cleanedData[k];
    });

    return cleanedData;
  };

  /* ------------------ Handle Step 1 Submit ------------------ */

  const handleStepOne = (data) => {
    const preparedData = prepareFormData(data);
    setFormDataPreview(preparedData);
    setCurrentStep(2);
  };

  /* ------------------ Handle Final Submit ------------------ */

  const handleFinalSubmit = () => {
    if (formDataPreview) {
      onSave(formDataPreview);
      handleClose();
    }
  };

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    setUploadedFile(null);
    setCurrentStep(1);
    setFormDataPreview(null);
    onClose();
  };

  const handleBackToEdit = () => {
    setCurrentStep(1);
  };

  /* ------------------ Check if save should be disabled ------------------ */

  const isSaveDisabled = () => {
    if (type === "file" && !item) {
      return isUploading || !uploadedFile;
    }
    return isUploading;
  };

  /* ------------------ Render ------------------ */

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {item ? `Edit Item: ${item.title}` : "Add New Item"}
            <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
              Step {currentStep} of 2:{" "}
              {currentStep === 1 ? "Enter Details" : "Confirm & Save"}
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody maxH="78vh" overflowY="auto">
            {currentStep === 1 ? (
              <ItemFormStep
                register={register}
                control={control}
                errors={errors}
                type={type}
                cardImg={cardImg}
                setValue={setValue}
                getValues={getValues}
                isUploading={isUploading}
                uploadedFile={uploadedFile}
                handleFileUpload={handleFileUpload}
                onSubmit={handleSubmit(handleStepOne)}
              />
            ) : (
              <ItemConfirmationStep
                formData={formDataPreview}
                isEditMode={!!item}
              />
            )}
          </ModalBody>

          <ModalFooter>
            {currentStep === 1 ? (
              <>
                <Button mr={3} onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="teal"
                  type="submit"
                  form="item-form"
                  isDisabled={isSaveDisabled()}
                  isLoading={isUploading}
                >
                  Next: Review
                </Button>
              </>
            ) : (
              <>
                <Button mr={3} onClick={handleBackToEdit}>
                  ← Back to Edit
                </Button>
                <Button colorScheme="teal" onClick={handleFinalSubmit}>
                  {item ? "Update Item" : "Create Item"}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Upload Confirmation Alert Dialog */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancelUpload}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm File Upload
            </AlertDialogHeader>

            <AlertDialogBody>
              {pendingFile && (
                <div>
                  <Text mb={2}>
                    <strong>File:</strong> {pendingFile.name}
                  </Text>
                  <Text mb={2}>
                    <strong>Size:</strong>{" "}
                    {(pendingFile.size / 1024).toFixed(2)} KB
                  </Text>
                  <Text>
                    <strong>Auth Group:</strong>{" "}
                    <Text as="span" color="teal.600" fontWeight="600">
                      {authGroup}
                    </Text>
                  </Text>
                </div>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelUpload}>
                Cancel
              </Button>
              <Button colorScheme="teal" onClick={confirmUpload} ml={3}>
                Yes, upload it!
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ItemFormModal;
